import React, { useEffect } from "react"
import { useRouter } from "next/router"
import { useTranslation, i18n } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { useDispatch, useSelector } from "react-redux"
import AemGrid from "@/components/AemGrid"
import {
  footerUrl,
  headerUrl,
  searchDataUrl,
  previewContentPrefix,
} from "@/constants"
import { aemAxiosInstance, aemPreviewAxiosInstance } from "@/constants/api"
import {
  getClienIP,
  getScriptsAndStyles,
  getAnalyticsScript,
  filterBackEndJson,
} from "@/utils/helper"
import { getCartCount, getAriaLabel } from "@/utils/cart"
import {
  selectGenericState,
  setBrandLogoImg,
  closeMenu,
  openMenu,
  setActiveMainCategory,
  setRegionNav,
  setGlobalScripts,
  setGlobalStyles,
  setAuthNav,
  setDatalayerPageName,
  setGbhDataLayerCustom,
} from "@/store/features/genericSlice"
import usePageAuth from "@/hooks/usePageAuth"
import { selectCartState } from "@/store/features/cartSlice"
import { getDataLayerObj } from "@/utils/helper"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import GlobalPageLayout from "@/components/Shared/GlobalPage/GlobalPageLayout"
import OuterSearchProgressive from "@/components/Search/v1/OuterSearchProgressive"
import SearchComponent from "@/components/Search/v1/Search"
import BreadCrumb from "@/components/Default/BreadCrumb"
/**
 * renders home page
 * @return {JSX.Element}
 * @param {object} props The props.
 */
const Search = props => {
  const {
    tmpData: data,
    headertmp: header,
    footertmp: footer,
    ip,
    adobeAnalytic,
    globalScript,
    footerScript,
    analyticsData,
    datalayerPageName,
    gbhDataLayerCustom,
  } = props
  const dispatch = useDispatch()
  const router = useRouter()
  const { cartUrl } = useSelector(selectCartState)
  const {
    brandLogoImg,
    menu: { isOpen, mainCategoryActive, isRegionOpen, isAuthNavOpen },
  } = useSelector(selectGenericState)
  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])
  usePageAuth()
  const cartCount = getCartCount()
  const { t } = useTranslation("common")
  const ariaLabel = getAriaLabel(t, cartCount)
  useEffect(() => {
    getScriptsAndStyles(footer)
  }, [footer])
  useEffect(() => {
    getAnalyticsScript(adobeAnalytic)
  }, [adobeAnalytic])
  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])
  useEffect(() => {
    dispatch(setDatalayerPageName(datalayerPageName))
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    return () => {
      dispatch(setDatalayerPageName(""))
      dispatch(setGbhDataLayerCustom(""))
    }
  }, [])
  const toggleMenu = () => {
    if (isOpen) {
      dispatch(closeMenu())
      dispatch(setAuthNav(false))
    } else {
      dispatch(openMenu())
    }
  }
  const redirect = () => {
    router.push(`${cartUrl}`)
  }
  const eventHandler = {
    store: {
      brandLogoImg: brandLogoImg,
      isOpen,
      mainCategoryActive,
      isRegionOpen,
      isAuthNavOpen,
      cartCount,
      ariaLabel,
      cartUrl,
    },
    callbacks: {
      setBrandLogoImg: path => {
        dispatch(setBrandLogoImg(path))
      },
      closeMenu: () => {
        dispatch(closeMenu())
      },
      setMainCategory: payload => {
        dispatch(setActiveMainCategory(payload))
      },
      toggleMenu: () => {
        toggleMenu()
      },
      setRegionNav: active => {
        dispatch(setRegionNav(active))
      },
      setAuthNav: active => {
        dispatch(setAuthNav(active))
      },
      setGlobalScripts: item => {
        dispatch(setGlobalScripts(item))
      },
      setGlobalStyles: item => {
        dispatch(setGlobalStyles(item))
      },
      onClick: () => {
        redirect()
      },
    },
  }
  return (
    <GlobalPageLayout
      header={
        <GlobalPageHeader
          header={header}
          title={data.title}
          globalScripting={globalScript}
        />
      }
      footer={<GlobalPageFooter footer={footer} footerScripting={footerScript} />}
    >
      <div id={data.id} className={data.cssClassNames}>
        <AemGrid
          data={data}
          count={0}
          eventHandler={eventHandler}
          breadCrumb={data => <BreadCrumb data={data} />}
          searchComponent={(data, eventHandler) => (
            <SearchComponent
              data={data}
              eventHandler={eventHandler}
              header={header}
            />
          )}
          searchFiller={() => <OuterSearchProgressive data={data} />}
        />
      </div>
    </GlobalPageLayout>
  )
}
/**
 *
 * @param {*} context
 * @return {Object}
 */
export async function getServerSideProps({
  req,
  res,
  query,
  resolvedUrl,
  locale,
}) {
  await i18n?.reloadResources()
  const isPreviewMode = req.cookies["aem-author-preview-mode"]
  const ip = getClienIP(req)
  const urlPath = req.url.split("?")[0] ?? ""
  const { data } = isPreviewMode
    ? await aemPreviewAxiosInstance({
        url: `${previewContentPrefix}/${searchDataUrl}`,
        method: "GET",
      })
    : await aemAxiosInstance({
        url: `/${locale}${searchDataUrl}`,
        method: "GET",
      })

  const globalScript = data?.globalScript || ""
  const footerScript = data?.footerScript || ""
  const topHeadScript = data?.topHeadScript ? data?.topHeadScript : null
  const analyticsScriptClass = data?.analyticsScriptClass
    ? data?.analyticsScriptClass
    : null

  const gbhDataLayerCustom = {}
  if (data?.datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
  }
  if (data?.eventPageType) {
    gbhDataLayerCustom.eventPageType = data?.eventPageType
  }
  if (data?.pageType) {
    gbhDataLayerCustom.pageType = data?.pageType
  }
  const { data: header } = isPreviewMode
    ? await aemPreviewAxiosInstance({
        url: `${data.headerExpFrg ? data.headerExpFrg : headerUrl}.model.json`,
        method: "GET",
      })
    : await aemAxiosInstance({
        url: `${data.headerExpFrg ? data.headerExpFrg : headerUrl}.model.json`,
        method: "GET",
      })
  const { data: footer } = isPreviewMode
    ? await aemPreviewAxiosInstance({
        url: `${data.footerExpFrg ? data.footerExpFrg : footerUrl}.model.json`,
        method: "GET",
      })
    : await aemAxiosInstance({
        url: `${data.footerExpFrg ? data.footerExpFrg : footerUrl}.model.json`,
        method: "GET",
      })
  const analyticsData = data?.data
  let tmpData = {}
  const adobeAnalytic = data?.adobeAnalytic || null
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }
  if (
    !data?.pageComponentProperties?.description &&
    data?.pageComponentProperties?.dataLayer &&
    data?.pageComponentProperties?.dataLayer[data?.pageComponentProperties?.id]
  ) {
    gbhDataLayerCustom.dataLayer =
      data?.pageComponentProperties?.dataLayer[
        data?.pageComponentProperties?.id
      ]
  }
  const datalayerPageName = data?.datalayerPageName
  tmpData = filterBackEndJson(tmpData)
  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  const metaContent = data?.metaContent ?? ""
  return {
    props: {
      ip,
      tmpData,
      headertmp,
      footertmp,
      adobeAnalytic,
      globalScript: globalScript.includes("<") ? globalScript : "",
      footerScript: footerScript.includes("<") ? footerScript : "",
      topHeadScript,
      analyticsData,
      analyticsScriptClass,
      datalayerPageName,
      gbhDataLayerCustom,
      urlPath,
      metaContent,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}
export default Search
